import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BigNumber from "bignumber.js";
import {
  fetchBeneficiaries,
  createBeneficiary,
  verifyBeneficiary,
  resendBeneficiaryOtp,
  removeBeneficiary,
  fetchSpotAedBalance,
  fetchFiatLimits,
  fetchVirtualAccountMe,
  formatAedAmount,
  parseFiatWithdrawLimits,
  previewFiatWithdrawal,
  submitFiatWithdrawal,
} from "../../../api/services/fiatService";
import { isPositiveMoneyString, newIdempotencyKey, toAedGrossString } from "../../../api/services/fiatMoney";
import FiatHoldBanner, { useFiatHold } from "../../../customComponents/FiatHoldBanner";
import { ProfileContext } from "../../../context/ProfileProvider";
import {
  fetchWithdrawalSecuritySettings,
  sendWithdrawalVerificationOtp,
} from "../../../api/services/withdrawService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import WithdrawFiatHistorySection from "./WithdrawFiatHistory";
import "../DepositFiatPage/depositfiat.css";
import "./withdrawFiat.css";

const HOW_STEPS = [
  {
    n: "1",
    title: "Add bank account",
    body: "Save the UAE IBAN where you want to receive AED. Only the masked account is shown after save.",
    icon: "/images/deposit-fiat-img/bank_icon2.svg",
  },
  {
    n: "2",
    title: "Verify email",
    body: "Confirm the IBAN with the email OTP. AGCX whitelist is created after a successful verify.",
    icon: "/images/deposit-fiat-img/fiat_bnr_icon.svg",
  },
  {
    n: "3",
    title: "Enter amount",
    body: "Enter the gross AED to send. Spot locks that amount. The bank receives net after a flat fee from the preview.",
    icon: "/images/deposit-fiat-img/rates_icon.svg",
  },
  {
    n: "4",
    title: "AED sent to bank",
    body: "Payouts go through AGCX Bank domestic rails to your verified IBAN.",
    icon: "/images/deposit-fiat-img/fiat_bnr_icon2.svg",
  },
];

const RESEND_COOLDOWN_SEC = 60;
const VERIFY_CODE_LEN = 6;
const EMPTY_LIMITS = {
  dailyRemaining: null,
  dailyLimit: null,
  dailyUsed: null,
  monthlyRemaining: null,
  monthlyLimit: null,
  monthlyUsed: null,
  minPerTx: null,
  maxPerTx: null,
};

function sanitizeAedAmount(raw) {
  const cleaned = String(raw || "").replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  const intPart = cleaned.slice(0, firstDot) || "0";
  const frac = cleaned.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
  return frac.length ? `${intPart}.${frac}` : `${intPart}.`;
}

function normalizePersonName(raw) {
  return String(raw || "")
    .normalize("NFKC")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function kycLegalName(vaAccount, userDetails) {
  const fromVa = String(vaAccount?.account_name || "").trim();
  if (fromVa) return fromVa;
  const first = String(userDetails?.firstName || userDetails?.first_name || "").trim();
  const last = String(userDetails?.lastName || userDetails?.last_name || "").trim();
  return `${first} ${last}`.trim();
}

function namesMatch(kycName, typed) {
  const a = normalizePersonName(kycName);
  const b = normalizePersonName(typed);
  return a.length >= 2 && a === b;
}

function moneyBn(value) {
  const s = String(value ?? "").trim().replace(/,/g, "");
  try {
    const n = new BigNumber(s);
    return n.isFinite() ? n : null;
  } catch {
    return null;
  }
}

function minPositiveMoney(values) {
  const nums = values.map(moneyBn).filter((n) => n && n.gte(0));
  if (!nums.length) return "";
  const m = BigNumber.min(...nums);
  if (m.lte(0)) return "";
  return m.toFixed(2);
}

function withdrawPreviewErrorMessage(err, minPerTx) {
  const code = err?.code || "";
  if (code === "FEE_SCHEDULE_MISSING") return "Withdrawals unavailable";
  if (code === "AMOUNT_BELOW_MIN") {
    const fromData = err?.data?.min ?? err?.data?.limits?.min ?? minPerTx;
    if (fromData != null && fromData !== "") {
      return `Minimum withdrawal is ${formatAedAmount(fromData)} AED.`;
    }
    return err?.message || "Amount is below the minimum.";
  }
  if (code === "AMOUNT_ABOVE_MAX" || code === "LIMIT_EXCEEDED") {
    return err?.message || (code === "LIMIT_EXCEEDED" ? "This would exceed your limit." : "Amount is above the maximum.");
  }
  return err?.message || "Could not load withdrawal quote.";
}

function previewFeeHint(preview) {
  if (!preview) return "";
  const type = String(preview.fee_type || "").toUpperCase();
  if (type === "FLAT" && preview.fee_aed != null && preview.fee_aed !== "") {
    return `Flat ${formatAedAmount(preview.fee_aed)} AED per withdrawal.`;
  }
  if (type === "BPS" && preview.bps != null && preview.bps !== "") {
    const bpsN = Number(preview.bps);
    if (Number.isFinite(bpsN)) return `${bpsN / 100}% (fee from quote).`;
  }
  return "";
}

function getEnabledVerifyMethods(settings) {
  const methods = settings?.methods;
  if (!methods) return ["email"];
  const enabled = [];
  if (methods.email?.enabled) enabled.push("email");
  if (methods.mobile?.enabled) enabled.push("mobile");
  if (methods.google_authenticator?.enabled) enabled.push("google_authenticator");
  if (methods.fund_password?.enabled) enabled.push("fund_password");
  return enabled;
}

function beneficiaryIbanLabel(ben) {
  if (!ben) return "••••";
  if (ben.iban_masked) return ben.iban_masked;
  if (ben.iban_last4) return `•••• ${ben.iban_last4}`;
  return "••••";
}

function StatusBadge({ status }) {
  if (status === "PENDING_OTP") {
    return <span className="fiat-withdraw-badge fiat-withdraw-badge--otp">Verify email</span>;
  }
  if (status === "VERIFIED") {
    return <span className="fiat-withdraw-badge fiat-withdraw-badge--verified">Verified</span>;
  }
  return null;
}

function DfFiatPageSkeleton({ variant = "withdraw" }) {
  const historyCols = variant === "withdraw" ? ["Date", "Amount", "Bank account", "Fee amount", "Action"] : ["Date", "Network", "Amount", "Status"];
  return (
    <div className="deposit-fiat-wrapper df-sk-page" aria-busy="true" aria-label="Loading">
      <section
        className="df-hero-section"
        style={{ backgroundImage: "url('/images/deposit-fiat-img/fiat_herobg.png')" }}
      >
        <div className="container df-hero-container">
          <div className="df-hero-content">
            <span className="df-sk-glass df-sk-glass--tag" />
            <span className="df-sk-glass df-sk-glass--h1" />
            <span className="df-sk-glass df-sk-glass--h1s" />
            <span className="df-sk-glass df-sk-glass--lead" />
            <div className="df-features-list">
              {[0, 1, 2].map((i) => (
                <div className="df-feature-item" key={i}>
                  <span className="df-sk-glass df-sk-glass--icon" />
                  <div className="df-feature-text">
                    <span className="df-sk-glass df-sk-glass--feat-title" />
                    <span className="df-sk-glass df-sk-glass--feat-p" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="df-hero-widget df-sk-widget">
            <div className="df-widget-header">
              <span className="df-sk-glass df-sk-glass--widget-title" />
              <span className="df-sk-glass df-sk-glass--pill" />
            </div>
            <div className="df-limit-list df-sk-glass-panel">
              <span className="df-sk-glass df-sk-glass--limit-label" />
              <span className="df-sk-glass df-sk-glass--line" />
              <span className="df-sk-glass df-sk-glass--line df-sk-w70" />
            </div>
            <div className="df-input-group">
              <span className="df-sk-glass df-sk-glass--label" />
              <div className="df-input-box df-sk-glass-panel df-sk-glass-panel--input" />
            </div>
            <span className="df-sk-glass df-sk-glass--label" />
            <div className="df-payment-method df-sk-glass-panel">
              <span className="df-sk-glass df-sk-glass--circle" />
              <div style={{ flex: 1 }}>
                <span className="df-sk-glass df-sk-glass--line df-sk-w60" />
                <span className="df-sk-glass df-sk-glass--line df-sk-w90" />
                <span className="df-sk-glass df-sk-glass--line df-sk-w80" />
              </div>
            </div>
            <span className="df-sk-glass df-sk-glass--submit" />
          </div>
        </div>
      </section>

      <section className="df-why-section">
        <div className="container">
          <span className="df-sk-glass df-sk-glass--section-title" />
          <div className="df-why-grid">
            {[0, 1, 2, 3].map((i) => (
              <div className="df-why-card df-sk-why-card" key={i}>
                <span className="df-sk-glass df-sk-glass--why-icon" />
                <span className="df-sk-glass df-sk-glass--line" />
                <span className="df-sk-glass df-sk-glass--line df-sk-w80" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="df-deposit-history-section">
        <div className="container">
          <div className="df-deposit-history-top">
            <span className="df-sk-glass df-sk-glass--hist-title" />
          </div>
          <div className="df-deposit-history-card">
            <div className="df-sk-table-wrap">
              <div className="df-sk-table-head">
                {historyCols.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
              {Array.from({ length: 6 }, (_, i) => (
                <div className="df-sk-row" key={i}>
                  <div className="df-sk-coin">
                    <span className="df-sk-circle df-sk-glass" />
                    <div className="df-sk-coin-txt">
                      <span className="df-sk-glass df-sk-bone--xs" />
                      <span className="df-sk-glass df-sk-bone--xxs" />
                    </div>
                  </div>
                  <span className="df-sk-glass df-sk-bone--cell" />
                  <span className="df-sk-glass df-sk-bone--cell" />
                  <span className="df-sk-glass df-sk-bone--cell df-sk-w50" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FiatRemoveAccountModal({ open, label, submitting, onClose, onConfirm }) {
  if (!open) return null;
  return (
    <div className="wf-verify-overlay wf-verify-overlay--glass" role="presentation" onClick={onClose}>
      <div
        className="wf-verify-modal wf-verify-modal--glass"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-remove-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="wf-verify-close" aria-label="Close" onClick={onClose}>
          <i className="ri-close-line" aria-hidden="true" />
        </button>
        <h3 id="wf-remove-title" className="wf-verify-title">
          Remove bank account?
        </h3>
        <p className="wf-verify-subtitle">
          Remove {label}? You can add the same IBAN again later.
        </p>
        <div className="wf-confirm-actions">
          <button type="button" className="fiat-withdraw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="wf-confirm-remove-btn"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? "Removing…" : "Remove"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FiatNoMethodsModal({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <div className="wf-verify-overlay" role="presentation" onClick={onClose}>
      <div className="wf-verify-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="wf-verify-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <h3 className="wf-verify-title">Enable a verification method</h3>
        <p className="wf-verify-subtitle">
          To withdraw AED, enable at least one security method: email, mobile, authenticator, or fund password.
        </p>
        <button
          type="button"
          className="df-submit-btn"
          onClick={() => {
            onClose();
            navigate("/user_profile/security?tab=email");
          }}
        >
          Go to security settings
        </button>
      </div>
    </div>
  );
}

function FiatWithdrawVerifyModal({
  open,
  methods,
  emailCode,
  mobileCode,
  authCode,
  fundPassword,
  fundVisible,
  otpSending,
  otpResend,
  fieldErrors,
  submitting,
  canSubmit,
  preview,
  onClose,
  onChangeEmail,
  onChangeMobile,
  onChangeAuth,
  onChangeFund,
  onToggleFundVisible,
  onSendOtp,
  onSubmit,
}) {
  if (!open) return null;
  return (
    <div className="wf-verify-overlay" role="presentation" onClick={onClose}>
      <div
        className="wf-verify-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Withdrawal security verification"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="wf-verify-close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <h3 className="wf-verify-title">Security verification</h3>
        <p className="wf-verify-subtitle">
          Complete all enabled methods. For email or SMS, tap Send code, then enter the OTP. Spot locks the gross amount.
        </p>
        {preview ? (
          <div className="wf-preview wf-verify-preview" aria-live="polite">
            <div className="wf-preview-row">
              <span>You send (gross)</span>
              <strong>{formatAedAmount(preview.amount)} AED</strong>
            </div>
            <div className="wf-preview-row">
              <span>Fee per withdrawal</span>
              <strong>{formatAedAmount(preview.fee_aed)} AED</strong>
            </div>
            <div className="wf-preview-row wf-preview-row--net">
              <span>You receive at bank</span>
              <strong>{formatAedAmount(preview.net_aed)} AED</strong>
            </div>
            {previewFeeHint(preview) ? <p className="wf-preview-hint">{previewFeeHint(preview)}</p> : null}
          </div>
        ) : null}

        {methods.includes("email") ? (
          <div className="wf-verify-field">
            <label htmlFor="wf-email-code">Email verification code</label>
            <div className="wf-verify-input-row">
              <input
                id="wf-email-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter email code"
                value={emailCode}
                onChange={(e) => onChangeEmail(e.target.value.replace(/\D/g, "").slice(0, VERIFY_CODE_LEN))}
              />
              <button
                type="button"
                className="wf-verify-send"
                disabled={otpSending.email || otpResend.email > 0}
                onClick={() => onSendOtp("email")}
              >
                {otpSending.email ? "Sending…" : otpResend.email > 0 ? `Resend in ${otpResend.email}s` : "Send code"}
              </button>
            </div>
            {fieldErrors.email ? <p className="wf-verify-field-error">{fieldErrors.email}</p> : null}
          </div>
        ) : null}

        {methods.includes("mobile") ? (
          <div className="wf-verify-field">
            <label htmlFor="wf-mobile-code">Mobile verification code</label>
            <div className="wf-verify-input-row">
              <input
                id="wf-mobile-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter mobile code"
                value={mobileCode}
                onChange={(e) => onChangeMobile(e.target.value.replace(/\D/g, "").slice(0, VERIFY_CODE_LEN))}
              />
              <button
                type="button"
                className="wf-verify-send"
                disabled={otpSending.mobile || otpResend.mobile > 0}
                onClick={() => onSendOtp("mobile")}
              >
                {otpSending.mobile ? "Sending…" : otpResend.mobile > 0 ? `Resend in ${otpResend.mobile}s` : "Send code"}
              </button>
            </div>
            {fieldErrors.mobile ? <p className="wf-verify-field-error">{fieldErrors.mobile}</p> : null}
          </div>
        ) : null}

        {methods.includes("google_authenticator") ? (
          <div className="wf-verify-field">
            <label htmlFor="wf-auth-code">Authenticator app</label>
            <input
              id="wf-auth-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Enter authenticator app code"
              value={authCode}
              onChange={(e) => onChangeAuth(e.target.value.replace(/\D/g, "").slice(0, VERIFY_CODE_LEN))}
            />
            {fieldErrors.google_authenticator ? (
              <p className="wf-verify-field-error">{fieldErrors.google_authenticator}</p>
            ) : null}
          </div>
        ) : null}

        {methods.includes("fund_password") ? (
          <div className="wf-verify-field">
            <label htmlFor="wf-fund-password">Fund password</label>
            <div className="wf-verify-input-row">
              <input
                id="wf-fund-password"
                type={fundVisible ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter fund password"
                value={fundPassword}
                onChange={(e) => onChangeFund(e.target.value)}
              />
              <button type="button" className="wf-verify-send" onClick={onToggleFundVisible}>
                {fundVisible ? "Hide" : "Show"}
              </button>
            </div>
            {fieldErrors.fund_password ? <p className="wf-verify-field-error">{fieldErrors.fund_password}</p> : null}
          </div>
        ) : null}

        <button type="button" className="df-submit-btn" disabled={submitting || !canSubmit} onClick={onSubmit}>
          {submitting ? "Submitting…" : "Confirm withdrawal"}
        </button>
        <div className="wf-verify-footer">
          <img src="/images/protected_icon.svg" alt="" width={16} height={16} />
          <span>Protected by Advanced Encryption</span>
        </div>
      </div>
    </div>
  );
}

export default function WithdrawFiatPage() {
  const hold = useFiatHold();
  const [searchParams] = useSearchParams();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = profileCtx.userDetails;
  const [loading, setLoading] = useState(true);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [step, setStep] = useState("main");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState("0");
  const [withdrawLimits, setWithdrawLimits] = useState(EMPTY_LIMITS);
  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [historyTick, setHistoryTick] = useState(0);
  const [securitySettings, setSecuritySettings] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [noMethodsOpen, setNoMethodsOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [emailVerifyCode, setEmailVerifyCode] = useState("");
  const [mobileVerifyCode, setMobileVerifyCode] = useState("");
  const [authAppCode, setAuthAppCode] = useState("");
  const [fundPassword, setFundPassword] = useState("");
  const [fundVisible, setFundVisible] = useState(false);
  const [otpSending, setOtpSending] = useState({ email: false, mobile: false });
  const [otpResend, setOtpResend] = useState({ email: 0, mobile: 0 });
  const [verifyFieldErrors, setVerifyFieldErrors] = useState({
    email: "",
    mobile: "",
    google_authenticator: "",
    fund_password: "",
  });

  const [iban, setIban] = useState("");
  const [accountName, setAccountName] = useState("");
  const [bankName, setBankName] = useState("");
  const [vaAccount, setVaAccount] = useState(null);
  const [otp, setOtp] = useState("");
  const [resendSec, setResendSec] = useState(0);
  const [otpSentTo, setOtpSentTo] = useState("email");
  const previewSeq = useRef(0);
  const idempotencyKeyRef = useRef("");

  const activeBeneficiary = useMemo(
    () => beneficiaries.find((b) => String(b.id) === String(selectedId)) || null,
    [beneficiaries, selectedId]
  );

  const verifiedReady = useMemo(
    () => beneficiaries.filter((b) => b.status === "VERIFIED"),
    [beneficiaries]
  );
  const pageReady = !loading;
  const kycName = useMemo(() => kycLegalName(vaAccount, userDetails), [vaAccount, userDetails]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    const [benRes, balRes, limitsRes, vaRes] = await Promise.all([
      fetchBeneficiaries(),
      fetchSpotAedBalance(),
      fetchFiatLimits(),
      fetchVirtualAccountMe(),
    ]);
    if (benRes.ok) {
      setBeneficiaries(benRes.items);
      setSelectedId((prev) => {
        if (benRes.items.some((b) => String(b.id) === String(prev))) return prev;
        const verified = benRes.items.find((b) => b.status === "VERIFIED");
        return String(verified?.id || benRes.items[0]?.id || "");
      });
    } else {
      setError(benRes.error?.message || "Could not load bank accounts.");
    }
    if (balRes.ok) setBalance(balRes.balance);
    if (limitsRes.ok) {
      setWithdrawLimits(parseFiatWithdrawLimits(limitsRes.limits));
    }
    setVaAccount(vaRes?.ok && vaRes.account ? vaRes.account : null);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithdrawalSecuritySettings();
        if (cancelled || !res?.success) return;
        setSecuritySettings(res.data?.settings || res.data || null);
      } catch {
        /* keep email fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (searchParams.get("add") === "1") setStep("add");
  }, [searchParams]);

  useEffect(() => {
    if (resendSec <= 0) return undefined;
    const t = window.setInterval(() => setResendSec((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [resendSec]);

  const enabledMethods = useMemo(() => getEnabledVerifyMethods(securitySettings), [securitySettings]);
  const amountGross = toAedGrossString(amount);
  const amountReady = !!amountGross && isPositiveMoneyString(amountGross);
  const maxWithdrawable = useMemo(
    () =>
      minPositiveMoney([
        balance,
        withdrawLimits.dailyRemaining,
        withdrawLimits.monthlyRemaining,
        withdrawLimits.maxPerTx,
      ]),
    [balance, withdrawLimits]
  );

  const verificationReady = useMemo(() => {
    if (!enabledMethods.length) return false;
    if (enabledMethods.includes("email") && !emailVerifyCode.trim()) return false;
    if (enabledMethods.includes("mobile") && !mobileVerifyCode.trim()) return false;
    if (enabledMethods.includes("google_authenticator") && !authAppCode.trim()) return false;
    if (enabledMethods.includes("fund_password") && !fundPassword.trim()) return false;
    return true;
  }, [enabledMethods, emailVerifyCode, mobileVerifyCode, authAppCode, fundPassword]);

  const otpResendActive = otpResend.email > 0 || otpResend.mobile > 0;
  useEffect(() => {
    if (!otpResendActive) return undefined;
    const id = window.setInterval(() => {
      setOtpResend((prev) => ({
        email: prev.email > 0 ? prev.email - 1 : 0,
        mobile: prev.mobile > 0 ? prev.mobile - 1 : 0,
      }));
    }, 1000);
    return () => window.clearInterval(id);
  }, [otpResendActive]);

  const resetVerifyFields = () => {
    setEmailVerifyCode("");
    setMobileVerifyCode("");
    setAuthAppCode("");
    setFundPassword("");
    setFundVisible(false);
    setVerifyFieldErrors({ email: "", mobile: "", google_authenticator: "", fund_password: "" });
  };

  useEffect(() => {
    setPreview(null);
    if (step !== "withdraw" || hold.held || !amountReady || !activeBeneficiary) {
      setPreviewing(false);
      setPreviewError("");
      return undefined;
    }
    const amt = moneyBn(amountGross);
    const avail = moneyBn(balance);
    if (amt && avail && amt.gt(avail)) {
      setPreviewing(false);
      setPreviewError("");
      return undefined;
    }
    if (withdrawLimits.maxPerTx != null) {
      const maxTx = moneyBn(withdrawLimits.maxPerTx);
      if (maxTx && amt && amt.gt(maxTx)) {
        setPreviewing(false);
        setPreviewError("");
        return undefined;
      }
    }
    if (withdrawLimits.minPerTx != null) {
      const minTx = moneyBn(withdrawLimits.minPerTx);
      if (minTx && amt && amt.lt(minTx)) {
        setPreviewing(false);
        setPreviewError("");
        return undefined;
      }
    }
    if (maxWithdrawable) {
      const cap = moneyBn(maxWithdrawable);
      if (cap && amt && amt.gt(cap)) {
        setPreviewing(false);
        setPreviewError("");
        return undefined;
      }
    }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    setPreviewError("");
    const t = window.setTimeout(async () => {
      const res = await previewFiatWithdrawal({
        amount: amountGross,
        whitelist_id: activeBeneficiary.id,
      });
      if (seq !== previewSeq.current) return;
      setPreviewing(false);
      if (!res.ok) {
        setPreview(null);
        setPreviewError(withdrawPreviewErrorMessage(res.error, withdrawLimits.minPerTx));
        return;
      }
      setPreview(res.preview);
    }, 400);
    return () => {
      window.clearTimeout(t);
      previewSeq.current += 1;
    };
  }, [
    step,
    hold.held,
    amountReady,
    amountGross,
    activeBeneficiary,
    balance,
    maxWithdrawable,
    withdrawLimits.minPerTx,
    withdrawLimits.maxPerTx,
  ]);

  const validateWithdrawAmount = () => {
    if (!amountReady) return "Enter an amount to withdraw.";
    const amt = moneyBn(amountGross);
    if (!amt || amt.lte(0)) return "Enter an amount to withdraw.";
    const avail = moneyBn(balance);
    if (amt && avail && amt.gt(avail)) return "Insufficient available balance.";
    if (withdrawLimits.minPerTx != null) {
      const minTx = moneyBn(withdrawLimits.minPerTx);
      if (minTx && amt.lt(minTx)) {
        return `Minimum withdrawal is ${formatAedAmount(withdrawLimits.minPerTx)} AED.`;
      }
    }
    if (withdrawLimits.maxPerTx != null) {
      const maxTx = moneyBn(withdrawLimits.maxPerTx);
      if (maxTx && amt.gt(maxTx)) {
        return `Maximum per transaction is ${formatAedAmount(withdrawLimits.maxPerTx)} AED.`;
      }
    }
    if (withdrawLimits.dailyRemaining != null) {
      const daily = moneyBn(withdrawLimits.dailyRemaining);
      if (daily && amt.gt(daily)) {
        return `Daily limit remaining is ${formatAedAmount(withdrawLimits.dailyRemaining)} AED.`;
      }
    }
    if (withdrawLimits.monthlyRemaining != null) {
      const monthly = moneyBn(withdrawLimits.monthlyRemaining);
      if (monthly && amt.gt(monthly)) {
        return `Monthly limit remaining is ${formatAedAmount(withdrawLimits.monthlyRemaining)} AED.`;
      }
    }
    if (maxWithdrawable) {
      const cap = moneyBn(maxWithdrawable);
      if (cap && amt.gt(cap)) {
        return `Maximum you can withdraw now is ${formatAedAmount(maxWithdrawable)} AED.`;
      }
    }
    if (previewError) return previewError;
    if (!preview) return previewing ? "Loading quote…" : "Enter an amount to see the fee quote.";
    return "";
  };

  const amountBlockReason = useMemo(() => {
    if (!amount || String(amount).endsWith(".")) return "invalid";
    if (!amountReady) return amount ? "invalid" : "";
    const amt = moneyBn(amountGross);
    if (!amt || amt.lte(0)) return "invalid";
    const avail = moneyBn(balance);
    if (amt && avail && amt.gt(avail)) return "insufficient";
    if (withdrawLimits.minPerTx != null) {
      const minTx = moneyBn(withdrawLimits.minPerTx);
      if (minTx && amt.lt(minTx)) return "min";
    }
    if (withdrawLimits.maxPerTx != null) {
      const maxTx = moneyBn(withdrawLimits.maxPerTx);
      if (maxTx && amt.gt(maxTx)) return "limit";
    }
    if (maxWithdrawable) {
      const cap = moneyBn(maxWithdrawable);
      if (cap && amt.gt(cap)) return "limit";
    }
    return "";
  }, [amount, amountReady, amountGross, balance, withdrawLimits.minPerTx, withdrawLimits.maxPerTx, maxWithdrawable]);

  const insufficientBalance = amountBlockReason === "insufficient";
  const belowMin = amountBlockReason === "min";
  const overLimit = amountBlockReason === "limit";
  const amountBlocked = !!amountBlockReason;
  const livePreview =
    preview &&
      !amountBlocked &&
      !previewError &&
      toAedGrossString(preview.amount) === amountGross
      ? preview
      : null;
  const canSubmitWithdraw =
    !hold.held &&
    !submitting &&
    !previewing &&
    !amountBlocked &&
    amountReady &&
    !!livePreview &&
    activeBeneficiary?.status === "VERIFIED";

  const mapVerifyError = (err) => {
    const code = err?.code || "";
    const message = err?.message || "Could not submit withdrawal.";
    const next = { email: "", mobile: "", google_authenticator: "", fund_password: "" };
    if (code === "EMAIL_OTP_INVALID" || code === "OTP_EXPIRED" || code === "OTP_NOT_FOUND") next.email = message;
    else if (code === "MOBILE_OTP_INVALID") next.mobile = message;
    else if (code === "GOOGLE_AUTH_INVALID") next.google_authenticator = message;
    else if (code === "FUND_PASSWORD_INVALID" || code === "FUND_PASSWORD_REQUIRED") next.fund_password = message;
    if (Object.values(next).some(Boolean)) {
      setVerifyFieldErrors(next);
      return true;
    }
    return false;
  };

  const handleSendVerifyOtp = async (method) => {
    if (method !== "email" && method !== "mobile") return;
    setOtpSending((p) => ({ ...p, [method]: true }));
    try {
      const res = await sendWithdrawalVerificationOtp(method);
      if (res?.success === false) {
        alertErrorMessage(res?.message || "Could not send code.");
        return;
      }
      setOtpResend((p) => ({ ...p, [method]: RESEND_COOLDOWN_SEC }));
      alertSuccessMessage(res?.message || "Verification code sent.");
    } catch {
      alertErrorMessage(method === "email" ? "Could not send email code." : "Could not send SMS code.");
    } finally {
      setOtpSending((p) => ({ ...p, [method]: false }));
    }
  };

  const handleConfirmWithdrawal = async () => {
    if (hold.held) return;
    if (!activeBeneficiary) return;
    if (!livePreview) {
      setError(previewError || "Wait for the fee quote before withdrawing.");
      return;
    }
    const amountError = validateWithdrawAmount();
    if (amountError) {
      setError(amountError);
      return;
    }
    if (!enabledMethods.length) {
      setVerifyOpen(false);
      setNoMethodsOpen(true);
      return;
    }
    const nextErrors = { email: "", mobile: "", google_authenticator: "", fund_password: "" };
    if (enabledMethods.includes("email") && !emailVerifyCode.trim()) nextErrors.email = "Enter the email OTP.";
    if (enabledMethods.includes("mobile") && !mobileVerifyCode.trim()) nextErrors.mobile = "Enter the mobile OTP.";
    if (enabledMethods.includes("google_authenticator") && !authAppCode.trim()) {
      nextErrors.google_authenticator = "Enter the authenticator app code.";
    }
    if (enabledMethods.includes("fund_password") && !fundPassword.trim()) {
      nextErrors.fund_password = "Enter your fund password.";
    }
    if (Object.values(nextErrors).some(Boolean)) {
      setVerifyFieldErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    setError("");
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = newIdempotencyKey("wd");
    const payload = {
      whitelist_id: activeBeneficiary.id,
      amount: livePreview?.amount || amountGross,
      idempotency_key: idempotencyKeyRef.current,
    };
    if (enabledMethods.includes("email")) payload.email_code = emailVerifyCode.trim();
    if (enabledMethods.includes("mobile")) payload.mobile_code = mobileVerifyCode.trim();
    if (enabledMethods.includes("google_authenticator")) payload.google_authenticator_code = authAppCode.trim();
    if (enabledMethods.includes("fund_password")) payload.fund_password = fundPassword.trim();

    const res = await submitFiatWithdrawal(payload);
    setSubmitting(false);
    if (!res.ok) {
      if (mapVerifyError(res.error)) {
        alertErrorMessage(res.error?.message || "Verification failed.");
        return;
      }
      const mapped = withdrawPreviewErrorMessage(res.error, withdrawLimits.minPerTx);
      setError(mapped);
      alertErrorMessage(mapped);
      if (res.error?.code === "AMOUNT_BELOW_MIN" || res.error?.code === "AMOUNT_ABOVE_MAX" || res.error?.code === "LIMIT_EXCEEDED" || res.error?.code === "FEE_SCHEDULE_MISSING") {
        setPreview(null);
        setPreviewError(mapped);
      }
      return;
    }

    const label = res.data?.status_label || res.message || "Withdrawal requested.";
    const reason = res.data?.status_reason || "";
    setVerifyOpen(false);
    resetVerifyFields();
    idempotencyKeyRef.current = "";
    setAmount("");
    setPreview(null);
    setStep("main");
    setInfo(reason ? `${label} ${reason}` : label);
    alertSuccessMessage(label);
    setHistoryTick((n) => n + 1);
    const [balRes, limitsRes] = await Promise.all([fetchSpotAedBalance(), fetchFiatLimits()]);
    if (balRes.ok) setBalance(balRes.balance);
    if (limitsRes.ok) setWithdrawLimits(parseFiatWithdrawLimits(limitsRes.limits));
  };

  const startWithdrawFlow = () => {
    if (hold.held) {
      setError("Fiat is paused. New withdrawals are unavailable.");
      return;
    }
    const ben =
      beneficiaries.find((b) => String(b.id) === String(selectedId) && b.status === "VERIFIED") ||
      null;
    if (!ben) {
      setError("Select a verified bank account to withdraw.");
      return;
    }
    const amountError = validateWithdrawAmount();
    if (step === "withdraw" && amountError) {
      setError(amountError);
      return;
    }
    setSelectedId(ben.id);
    setError("");
    setInfo("");
    if (step !== "withdraw") {
      setStep("withdraw");
      return;
    }
    if (!livePreview || previewing) {
      setError(previewError || "Wait for the fee quote before withdrawing.");
      return;
    }
    if (!enabledMethods.length) {
      setNoMethodsOpen(true);
      return;
    }
    resetVerifyFields();
    idempotencyKeyRef.current = newIdempotencyKey("wd");
    setVerifyOpen(true);
  };

  const handleRemoveAccount = (id) => {
    if (!id || hold.held) return;
    const ben = beneficiaries.find((b) => String(b.id) === String(id));
    if (!ben) return;
    setError("");
    setInfo("");
    setRemoveTarget(ben);
  };

  const closeRemoveModal = () => {
    if (submitting) return;
    setRemoveTarget(null);
  };

  const confirmRemoveAccount = async () => {
    const id = removeTarget?.id;
    if (!id || hold.held) return;

    setSubmitting(true);
    setError("");
    setInfo("");
    const res = await removeBeneficiary(id);
    setSubmitting(false);
    if (!res.ok) {
      setRemoveTarget(null);
      setError(res.error?.message || "Could not remove bank account.");
      return;
    }
    setBeneficiaries((prev) => prev.filter((b) => String(b.id) !== String(id)));
    if (String(selectedId) === String(id)) {
      setSelectedId("");
      setStep("main");
    }
    setRemoveTarget(null);
    setInfo("Bank account removed.");
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (hold.held) return;
    if (accountName.trim().length < 2) {
      setError("Enter the account holder name as shown on the bank account.");
      return;
    }
    if (!normalizePersonName(kycName)) {
      setError("Complete KYC and create your deposit IBAN before adding a withdrawal account.");
      return;
    }
    if (!namesMatch(kycName, accountName)) {
      setError(`Account holder name must match your KYC name: ${kycName}`);
      return;
    }
    setSubmitting(true);
    setError("");
    setInfo("");
    const res = await createBeneficiary({
      iban: iban.replace(/\s+/g, "").toUpperCase(),
      account_name: accountName.trim(),
      bank_name: bankName.trim(),
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error?.message || "Could not save bank account.");
      return;
    }
    setBeneficiaries((prev) => {
      const next = prev.filter((b) => b.id !== res.beneficiary.id);
      return [res.beneficiary, ...next];
    });
    setSelectedId(res.beneficiary.id);
    setIban("");
    setAccountName("");
    setBankName("");
    setOtp("");
    setOtpSentTo(res.otpSentTo || "email");
    setResendSec(RESEND_COOLDOWN_SEC);
    setStep("otp");
    setInfo("Verification code sent to your registered email.");
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (hold.held || !selectedId || otp.trim().length !== 6) return;
    setSubmitting(true);
    setError("");
    setInfo("");
    const res = await verifyBeneficiary(selectedId, otp.trim());
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error?.message || "Invalid verification code.");
      return;
    }
    setBeneficiaries((prev) => prev.map((b) => (b.id === res.beneficiary.id ? res.beneficiary : b)));
    setOtp("");
    setStep("main");
    setInfo("Bank account verified. You can withdraw AED to this IBAN.");
  };

  const handleResendOtp = async () => {
    if (hold.held || !selectedId || resendSec > 0) return;
    setError("");
    const res = await resendBeneficiaryOtp(selectedId);
    if (!res.ok) {
      setError(res.error?.message || "Could not resend code.");
      return;
    }
    setOtpSentTo(res.otpSentTo || "email");
    setResendSec(RESEND_COOLDOWN_SEC);
    setInfo("A new verification code was sent to your email.");
  };

  const handleSelectAccount = (ben) => {
    setSelectedId(ben.id);
    setError("");
    setInfo("");
  };

  const continueSelectedAccount = (ben) => {
    handleSelectAccount(ben);
    if (ben.status === "PENDING_OTP") {
      setStep("otp");
      setOtp("");
    }
  };

  const startAddAccount = () => {
    if (hold.held) return;
    setStep("add");
    setAccountName(kycName);
    setError("");
    setInfo("");
  };

  const renderWidgetBody = () => {
    if (loading) {
      return <div className="fiat-withdraw-loading">Loading…</div>;
    }

    if (step === "add") {
      return (
        <form className="fiat-withdraw-widget" onSubmit={handleAddAccount}>
          <div className="fiat-withdraw-step-label">Step 1 — Add bank account</div>
          <p className="fiat-withdraw-help">
            Enter the UAE bank account where you want to receive AED withdrawals. After save, only the masked IBAN is shown.
          </p>
          <div className="fiat-withdraw-field">
            <label htmlFor="fiat-account-name">Account holder name</label>
            <input
              id="fiat-account-name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="Must match your KYC name"
              required
              minLength={2}
              disabled={hold.held || submitting}
            />
            <p className="fiat-withdraw-field-hint">
              {kycName
                ? `Must match your KYC name: Bhavikkumar Hareshkumar ValaHareshkumar Vallabhbhai Vala ${kycName}`
                : "Complete KYC and create your deposit IBAN first. The name must match KYC."}
            </p>
          </div>
          <div className="fiat-withdraw-field">
            <label htmlFor="fiat-iban">Account number (IBAN)</label>
            <input
              id="fiat-iban"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="AE070331234567890123456"
              required
              autoComplete="off"
              disabled={hold.held || submitting}
            />
          </div>
          <div className="fiat-withdraw-field">
            <label htmlFor="fiat-bank-name">Bank name</label>
            <input
              id="fiat-bank-name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="e.g. Emirates NBD"
              required
              disabled={hold.held || submitting}
            />
          </div>
          <div className="fiat-withdraw-actions">
            <button type="submit" className="df-submit-btn" disabled={hold.held || submitting}>
              {submitting ? "Saving…" : "Send verification code"} <i class="ri-arrow-right-line"></i>
            </button>
            <button
              type="button"
              className="fiat-withdraw-btn"
              onClick={() => {
                setStep("main");
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      );
    }

    if (step === "otp" && activeBeneficiary) {
      return (
        <form className="fiat-withdraw-widget" onSubmit={handleVerifyOtp}>
          <div className="fiat-withdraw-step-label">Step 2 — Verify email</div>
          <p className="fiat-withdraw-help">
            Enter the 6-digit code sent to your registered {otpSentTo === "email" ? "email" : "contact"}.
            This confirms the IBAN and adds it to the AGCX whitelist.
          </p>
          <div className="fiat-withdraw-bank-card selected fiat-withdraw-bank-card--static">
            <span className="wf-account-radio" aria-hidden="true" />
            <div className="fiat-withdraw-bank-card__body">
              <div className="fiat-withdraw-bank-card__name">
                {activeBeneficiary.account_name_masked || "Bank account"}
              </div>
              <div className="fiat-withdraw-bank-card__meta">
                {beneficiaryIbanLabel(activeBeneficiary)}
                {activeBeneficiary.bank_name ? ` · ${activeBeneficiary.bank_name}` : ""}
              </div>
            </div>
          </div>
          <div className="fiat-withdraw-field verificationcode_block">
            <label htmlFor="fiat-otp">Verification code</label>
            <div className="fiat-withdraw-otp-row">
              <input
                id="fiat-otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                disabled={hold.held || submitting}
              />
              <button
                type="button"
                className="fiat-withdraw-resend"
                onClick={handleResendOtp}
                disabled={hold.held || resendSec > 0 || submitting}
              >
                {resendSec > 0 ? `Resend in ${resendSec}s` : "Resend code"}
              </button>
            </div>
          </div>
          <div className="fiat-withdraw-actions">
            <button type="submit" className="df-submit-btn" disabled={hold.held || submitting || otp.length !== 6}>
              {submitting ? "Verifying…" : "Verify email"}
            </button>
            <button type="button" className="fiat-withdraw-btn" onClick={() => setStep("main")}>
              Back
            </button>
            <button
              type="button"
              className="fiat-withdraw-btn"
              onClick={() => handleRemoveAccount(activeBeneficiary.id)}
              disabled={hold.held || submitting}
            >
              Remove account
            </button>
          </div>
        </form>
      );
    }

    if (step === "withdraw" && activeBeneficiary) {
      return (
        <div className="fiat-withdraw-widget">
          <div className="fiat-withdraw-step-label">Withdraw AED</div>
          <p className="subaccountbnk">Add a UAE bank account to withdraw AED from your spot wallet.</p>
          <div className="fiat-withdraw-bank-card selected fiat-withdraw-bank-card--static">
            <span className="wf-account-radio" aria-hidden="true" />
            <div className="fiat-withdraw-bank-card__body">
              <div className="fiat-withdraw-bank-card__top">
                <span className="fiat-withdraw-bank-card__name">{activeBeneficiary.account_name_masked}</span>
                <StatusBadge status="VERIFIED" />
              </div>
              <div className="fiat-withdraw-bank-card__meta">
                {beneficiaryIbanLabel(activeBeneficiary)}
                {activeBeneficiary.bank_name ? ` · ${activeBeneficiary.bank_name}` : ""}
              </div>
              <div className="fiat-withdraw-bank-card__actions">
                <button
                  type="button"
                  className="fiat-withdraw-hide-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemoveAccount(activeBeneficiary.id);
                  }}
                  disabled={hold.held || submitting}
                >
                  Remove &gt;
                </button>
              </div>
            </div>
          </div>

          <p
            className={`fiat-withdraw-help right_alien${insufficientBalance ? " fiat-withdraw-help--error" : ""
              }`}
          >
            Available: {formatAedAmount(balance)} AED
            {insufficientBalance ? " · Not enough for this withdrawal" : ""}
          </p>

          <div className="fiat-withdraw-field">
            <label htmlFor="fiat-withdraw-amount">Enter amount</label>
            <div className="wf-amount-box">
              <input
                id="fiat-withdraw-amount"
                value={amount}
                onChange={(e) => setAmount(sanitizeAedAmount(e.target.value))}
                placeholder="0.00"
                disabled={hold.held}
                inputMode="decimal"
              />
              <div className="wf-amount-box__side">
                <span>AED</span>
                <button
                  type="button"
                  className="wf-max-btn"
                  onClick={() => setAmount(maxWithdrawable || "")}
                  disabled={hold.held || !maxWithdrawable}
                >
                  Max
                </button>
              </div>
            </div>
            {/* {withdrawLimits.minPerTx != null ? (
              <span className={`fiat-withdraw-limit${belowMin ? " fiat-withdraw-limit--error" : ""}`}>
                Minimum withdrawal is {formatAedAmount(withdrawLimits.minPerTx)} AED.
              </span>
            ) : null}
            {overLimit && maxWithdrawable ? (
              <span className="fiat-withdraw-limit fiat-withdraw-limit--error">
                Max now: {formatAedAmount(maxWithdrawable)} AED (balance / limits)
              </span>
            ) : null} */}
          </div>
          {previewError ? <p className="fiat-withdraw-help fiat-withdraw-help--error">{previewError}</p> : null}
          <div className="wf-preview" aria-live="polite">
            <div className="wf-preview-row">
              <span>You send (gross)</span>
              <strong>{livePreview ? `${formatAedAmount(livePreview.amount)} AED` : "—"}</strong>
            </div>
            <div className="wf-preview-row">
              <span>Fee per withdrawal</span>
              <strong>{livePreview ? `${formatAedAmount(livePreview.fee_aed)} AED` : "—"}</strong>
            </div>
            <div className="wf-preview-row wf-preview-row--net">
              <span>You receive at bank</span>
              <strong>{livePreview ? `${formatAedAmount(livePreview.net_aed)} AED` : "—"}</strong>
            </div>
            {livePreview && previewFeeHint(livePreview) ? (
              <p className="wf-preview-hint">{previewFeeHint(livePreview)}</p>
            ) : null}
            {previewing && amountReady && !amountBlocked ? (
              <p className="fiat-withdraw-help">Loading fee quote…</p>
            ) : null}
          </div>
          <button
            type="button"
            className="df-submit-btn"
            onClick={startWithdrawFlow}
            disabled={!canSubmitWithdraw}
          >
            {submitting
              ? "Submitting…"
              : insufficientBalance
                ? "Insufficient balance"
                : belowMin
                  ? "Below minimum"
                  : overLimit
                    ? "Over limit"
                    : "Withdraw"} <i class="ri-arrow-right-line"></i>
          </button>
          <button type="button" className="fiat-withdraw-link-btn" onClick={() => setStep("main")}>
            Change bank account
          </button>
        </div>
      );
    }

    return (
      <div className="fiat-withdraw-widget">
        <div className="fiat-withdraw-step-label">Bank Accounts</div>
        <p className="subaccountbnk">Add a UAE bank account to withdraw AED from your spot wallet.</p>
        {beneficiaries.length === 0 ? (
          <>
            <p className="fiat-withdraw-help">
              Add a UAE bank account to withdraw AED from your spot wallet.
            </p>
            <button type="button" className="df-submit-btn" onClick={startAddAccount} disabled={hold.held}>
              Add bank account <i class="ri-arrow-right-line"></i>
            </button>
          </>
        ) : (
          <>
            {beneficiaries.map((ben) => {
              const isSelected = String(selectedId) === String(ben.id);
              const needsContinue = ben.status === "PENDING_OTP";
              return (
                <label
                  key={ben.id}
                  className={`fiat-withdraw-bank-card${isSelected ? " selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="fiat-whitelist-account"
                    className="wf-account-radio-input"
                    checked={isSelected}
                    onChange={() => handleSelectAccount(ben)}
                  />
                  <span className="wf-account-radio" aria-hidden="true" />
                  <div className="fiat-withdraw-bank-card__body">
                    <div className="fiat-withdraw-bank-card__top">
                      <span className="fiat-withdraw-bank-card__name">{ben.account_name_masked || "Bank account"}</span>
                      <StatusBadge status={ben.status} />
                    </div>
                    <div className="fiat-withdraw-bank-card__meta">
                      {beneficiaryIbanLabel(ben)}
                      {ben.bank_name ? ` · ${ben.bank_name}` : ""}
                    </div>
                    <div className="fiat-withdraw-bank-card__actions">
                      {needsContinue ? (
                        <button
                          type="button"
                          className="wf-select-btn wf-select-btn--ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            continueSelectedAccount(ben);
                          }}
                        >
                          Verify email
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="fiat-withdraw-hide-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveAccount(ben.id);
                        }}
                        disabled={hold.held || submitting}
                      >
                        Remove &gt;
                      </button>
                    </div>
                  </div>
                </label>
              );
            })}
            <div className="fiat-withdraw-actions">
              <button
                type="button"
                className="df-submit-btn"
                onClick={startWithdrawFlow}
                disabled={hold.held || !verifiedReady.some((b) => String(b.id) === String(selectedId))}
              >
                Withdraw <i class="ri-arrow-right-line"></i>
              </button>
              <button type="button" className="fiat-withdraw-btn adbankbtn" onClick={startAddAccount} disabled={hold.held}>
                Add bank account
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard_right fiatmobile">
      {!pageReady ? (
        <DfFiatPageSkeleton variant="withdraw" />
      ) : (
        <div className="deposit-fiat-wrapper">
          <section
            className="df-hero-section"
            style={{ backgroundImage: "url('/images/deposit-fiat-img/fiat_herobg.png')" }}
          >
            <div className="container df-hero-container">
              <div className="df-hero-content">
                <FiatHoldBanner />
                {/* <div className="df-gateway-tag">FIAT GATEWAY</div> */}
                <h1>
                  AED Withdrawals
                  <br />
                  to Your Bank
                </h1>
                <p>Withdraw AED from your spot wallet to a verified UAE bank account securely.</p>

                <div className="df-features-list">
                  <div className="df-feature-item">
                    <div className="df-feature-icon">
                      <img class="light_img" src="/images/deposit-fiat-img/fiat_bnr_icon.png" alt="" />
                      <img class="dark_img" src="/images/deposit-fiat-img/fiat_bnr_icon_light.png" alt="" />
                    </div>
                    <div className="df-feature-text">
                      <h4>Secure & Reliable</h4>
                      <p>Protected with advanced security syEmail OTP confirms your IBAN, then it is whitelisted for AED payouts.stems.</p>
                    </div>
                  </div>
                  <div className="df-feature-item">
                    <div className="df-feature-icon">
                      <img class="light_img" src="/images/deposit-fiat-img/fiat_bnr_icon2.png" alt="" />
                      <img class="dark_img" src="/images/deposit-fiat-img/fiat_bnr_icon2_light.png" alt="" />
                    </div>
                    <div className="df-feature-text">
                      <h4>Verified Accounts Only</h4>
                      <p>Whitelist your IBAN once, then withdraw when you need.</p>
                    </div>
                  </div>
                  <div className="df-feature-item">
                    <div className="df-feature-icon">
                      <img class="light_img" src="/images/deposit-fiat-img/fiat_bnr_icon3.png" alt="" />
                      <img class="dark_img" src="/images/deposit-fiat-img/fiat_bnr_icon3_light.png" alt="" />
                    </div>
                    <div className="df-feature-text">
                      <h4>UAE AED Rail</h4>
                      <p>Dedicated AGXC virtual IBAN.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="df-hero-widget">
                <div className="df-widget-header">
                  <h3>Withdraw Fiat</h3>
                  <Link to="/asset_managemnet/deposit_fiat" className="df-withdrawal-btn">
                    Fiat Deposit
                  </Link>
                </div>

                {error ? <div className="fiat-withdraw-alert fiat-withdraw-alert--error">{error}</div> : null}
                {info ? <div className="fiat-withdraw-alert fiat-withdraw-alert--info">{info}</div> : null}

                {renderWidgetBody()}
              </div>
            </div>
          </section>

          <section className="df-why-section" aria-labelledby="wf-how-title">
            <div className="container">
              <h2 id="wf-how-title" className="df-section-title">
                How to withdraw <span>Fiat</span>?
              </h2>
              <div className="df-why-grid">
                {HOW_STEPS.map((step) => (
                  <div className="df-why-card" key={step.n}>
                    <img class="afterarrow" src="/images/deposit-fiat-img/boxafter_arrow.png" alt="" />
                    <div className="tophd_how d-flex align-items-center">
                      <div className="df-why-icon">
                        <img src={step.icon} alt="" />
                      </div>
                      <h4>
                        {step.title}
                      </h4>
                    </div>
                    <p>{step.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <WithdrawFiatHistorySection refreshKey={historyTick} />
        </div>
      )}

      <FiatWithdrawVerifyModal
        open={verifyOpen}
        methods={enabledMethods}
        emailCode={emailVerifyCode}
        mobileCode={mobileVerifyCode}
        authCode={authAppCode}
        fundPassword={fundPassword}
        fundVisible={fundVisible}
        otpSending={otpSending}
        otpResend={otpResend}
        fieldErrors={verifyFieldErrors}
        submitting={submitting}
        canSubmit={verificationReady}
        preview={livePreview}
        onClose={() => setVerifyOpen(false)}
        onChangeEmail={(v) => {
          setEmailVerifyCode(v);
          setVerifyFieldErrors((p) => ({ ...p, email: "" }));
        }}
        onChangeMobile={(v) => {
          setMobileVerifyCode(v);
          setVerifyFieldErrors((p) => ({ ...p, mobile: "" }));
        }}
        onChangeAuth={(v) => {
          setAuthAppCode(v);
          setVerifyFieldErrors((p) => ({ ...p, google_authenticator: "" }));
        }}
        onChangeFund={(v) => {
          setFundPassword(v);
          setVerifyFieldErrors((p) => ({ ...p, fund_password: "" }));
        }}
        onToggleFundVisible={() => setFundVisible((v) => !v)}
        onSendOtp={handleSendVerifyOtp}
        onSubmit={handleConfirmWithdrawal}
      />
      <FiatNoMethodsModal open={noMethodsOpen} onClose={() => setNoMethodsOpen(false)} />
      <FiatRemoveAccountModal
        open={Boolean(removeTarget)}
        label={beneficiaryIbanLabel(removeTarget)}
        submitting={submitting}
        onClose={closeRemoveModal}
        onConfirm={confirmRemoveAccount}
      />
    </div>
  );
}
